import { ListSubheader, Menu, MenuItem } from "@mui/material";
import { useSelector } from "react-redux";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch.ts";
import {
  selectConditions,
  selectSelectedStatementLineId,
  selectStatementAxes,
  selectStatementDataColumns,
  selectStatementLines,
} from "../../../../store/selectors/fieldsStateSelectors.ts";
import { selectMeasures } from "../../../../store/selectors/metaDataSelectors.ts";
import { createFinancialStatementAction } from "../../../../store/utils/storeUtils.ts";
import { createConditionField } from "../../common/utilities/createFields.ts";
import useAxisOutcomeHandler from "../columns/useAxisOutcomeHandler.ts";
import { resolveFieldForAxes } from "../utils/statementColumns.ts";
import type { AddToSource, AddToTarget } from "../utils/statementRowsArea.ts";
import {
  createMeasureGroupLines,
  findAmountMeasure,
  findMemoAmountMeasure,
  getNewLineAnchor,
  resolveDimensionGroupForRows,
  resolveFieldForRows,
} from "../utils/statementRowsArea.ts";
import { resolveDimensionGroupForAxes } from "./resolveDimensionGroupForAxes.ts";

interface Props {
  anchorEl: HTMLElement;
  source: AddToSource;
  targets: AddToTarget[];
  onClose: () => void;
}

const AddToMenu = ({ anchorEl, source, targets, onClose }: Props) => {
  const dispatch = useAppDispatch();
  const { sendNotification } = useNotificationContext();
  const selectedLineId = useSelector(selectSelectedStatementLineId);
  const axes = useSelector(selectStatementAxes);
  const lines = useSelector(selectStatementLines);
  const dataColumns = useSelector(selectStatementDataColumns);
  const measures = useSelector(selectMeasures);
  const conditions = useSelector(selectConditions);
  const handleAxisOutcome = useAxisOutcomeHandler();

  const handleRowsClick = () => {
    onClose();
    const anchor = getNewLineAnchor(selectedLineId);
    if (source.kind === "measureGroup") {
      const lines = createMeasureGroupLines(source.caption, source.measures);
      dispatch(createFinancialStatementAction("addLines", { lines, anchor }));
      return;
    }
    const rowsContext = {
      axes,
      amountMeasure: findAmountMeasure(measures),
      memoAmountMeasure: findMemoAmountMeasure(measures),
    };
    const outcome =
      source.kind === "dimensionGroup"
        ? resolveDimensionGroupForRows(source.caption, source.dimensions, rowsContext)
        : resolveFieldForRows(source, rowsContext);
    if (outcome.kind === "refuse") {
      sendNotification(outcome.message);
      return;
    }
    dispatch(createFinancialStatementAction("addLines", { lines: outcome.lines, anchor }));
  };

  const handleColumnsClick = () => {
    onClose();
    const axesContext = { axes, lines, dataColumns };
    if (source.kind === "dimensionGroup") {
      resolveDimensionGroupForAxes(source.dimensions, axesContext).forEach(handleAxisOutcome);
      return;
    }
    handleAxisOutcome(resolveFieldForAxes(source, axesContext));
  };

  const handleConditionsClick = () => {
    onClose();
    if (source.kind === "dimension") {
      const field = createConditionField(source.dimension);
      dispatch(createFinancialStatementAction("addCondition", { field, index: conditions.length }));
    }
  };

  return (
    <Menu open anchorEl={anchorEl} onClose={onClose}>
      <ListSubheader sx={{ lineHeight: 2, bgcolor: "transparent" }}>Add to</ListSubheader>
      {targets.includes("Rows") && <MenuItem onClick={handleRowsClick}>Rows</MenuItem>}
      {targets.includes("Columns") && <MenuItem onClick={handleColumnsClick}>Columns</MenuItem>}
      {targets.includes("Conditions") && <MenuItem onClick={handleConditionsClick}>Conditions</MenuItem>}
    </Menu>
  );
};

export default AddToMenu;
